import { useTranslation } from "@/core/i18n";
import { components } from "api/generated";
import cx from "classnames";
import { FC, ReactNode, SVGProps } from "react";
import { Link } from "react-router-dom";

import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import Billing from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import Check from "atoms/icon/icons/ic_check_xsmall_ol.svg";
import SwapHorizontal from "atoms/icon/icons/ic_swap_horizontal.svg";
import Snowflake from "atoms/icon/icons/ic_snowflake_small_ol.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { TextButton } from "atoms/text-button/TextButton";
import dayjs from "dayjs";
import {
  getDunningDays,
  hasCurrentMembershipBillingPeriod,
  isFreezePending,
  isFreezeScheduled,
  isMembershipFrozen,
} from "pages/root/wallet/Memberships/membershipHelper";
import { useFeatureFlag } from "services/Flags";
import {
  formatBillingPeriodInterval,
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import { hasCancellationRunway, hasFutureScheduledCancellation } from "utils/membershipStatus";

interface ManageMembershipCardProps {
  disableCancellationAllowed: boolean;
  platformUserMembership: components["schemas"]["PlatformUserMembershipModel"];
  paymentMethodLast4?: string | null;
  onKeepMembership: () => void;
  onUpdatePaymentMethod: () => void;
  onReactivateMembership: () => void;
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"];
  onRetryMembershipPayment: (platformUserMembershipId: string) => void;
  onKeepCurrentMembership: (platformUserMembershipId: string) => void;
  isKeepingCurrentMembership?: boolean;
  isRetryingMembershipPayment?: boolean;
  freeze?: components["schemas"]["DisplayMembershipFreeze"] | null;
  onWithdrawFreezeRequest: (platformUserMembershipId: string, membershipFreezeId: string) => void;
  withdrawPending?: boolean;
  onResumeMembership: (platformUserMembershipId: string, membershipFreezeId: string) => void;
}

export enum MembershipStatus {
  active = "active",
  willCancel = "willCancel",
  canceled = "canceled",
  notAvailable = "notAvailable",
  dunning = "dunning",
  freezePending = "freezePending",
  freezeScheduled = "freezeScheduled",
  frozen = "frozen",
}

export const getMembershipStatus = (
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"],
  membershipActive: boolean,
  freeze?: components["schemas"]["DisplayMembershipFreeze"] | null,
): MembershipStatus => {
  const willBeCanceled = hasFutureScheduledCancellation(membershipStatus.willCancelAt);
  const isCanceled = membershipStatus.canceled;
  const isNotAvailable = isCanceled && membershipActive === false;
  const isInDunning = membershipStatus.dunningStage && membershipStatus.enteredDunningAt;
  // A canceled membership without runway is dead and must not render payment-failure CTAs.
  const isTerminallyCanceled = !hasCancellationRunway(membershipStatus);

  let status: MembershipStatus;

  if (isNotAvailable) {
    status = MembershipStatus.notAvailable;
  } else if (isInDunning && !isTerminallyCanceled) {
    status = MembershipStatus.dunning;
  } else if (willBeCanceled && !isTerminallyCanceled) {
    status = MembershipStatus.willCancel;
  } else if (isCanceled) {
    status = MembershipStatus.canceled;
  } else if (isFreezePending(freeze)) {
    status = MembershipStatus.freezePending;
  } else if (isFreezeScheduled(freeze)) {
    status = MembershipStatus.freezeScheduled;
  } else if (isMembershipFrozen(freeze)) {
    status = MembershipStatus.frozen;
  } else {
    status = MembershipStatus.active;
  }
  return status;
};

const VISIBLE_BENEFITS_COUNT = 3;
const DATE_FORMAT = "MMM D, YYYY";
const PENDING_CHANGE_STEP_KEY = "pendingChange";

type StatusTone = "brand" | "alert" | "muted";

const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  brand: "bg-brand-color/10 text-brand-color",
  alert: "bg-red/10 text-red",
  muted: "bg-light-grey text-secondary",
};

const StatusPill = ({ tone, children }: { tone: StatusTone; children: string }) => (
  <span
    className={cx(
      "shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-body3 font-semibold",
      STATUS_TONE_CLASSES[tone],
    )}
  >
    {children}
  </span>
);

const CHIP_CLASS =
  "flex items-center gap-x-1.5 rounded-full border border-black/10 bg-white px-3 py-2 text-body2 text-primary transition-opacity active:opacity-60";

const ActionChip = ({
  icon,
  to,
  onClick,
  children,
}: {
  icon: FC<SVGProps<SVGSVGElement>>;
  to?: string;
  onClick?: () => void;
  children: string;
}) => {
  const content = (
    <>
      <Icon svg={icon} color="brandColor" size="x3small" />
      {children}
    </>
  );

  return to ? (
    <Link to={to} className={CHIP_CLASS}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={CHIP_CLASS}>
      {content}
    </button>
  );
};

interface TimelineStep {
  key: string;
  date: dayjs.Dayjs;
  title: string;
  detail?: ReactNode;
}

const Timeline = ({ steps }: { steps: TimelineStep[] }) => (
  <ol className="flex flex-col">
    {steps.map((step, index) => (
      <li key={step.key} className="relative flex gap-x-3 pb-4 last:pb-0">
        {index < steps.length - 1 && (
          <span className="absolute left-[5px] top-4 h-full w-px bg-black/10" aria-hidden />
        )}
        <span
          className={cx(
            "mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2",
            index === 0 ? "border-brand-color bg-brand-color" : "border-black/20 bg-white",
          )}
          aria-hidden
        />
        <div className="flex flex-1 flex-col">
          <div className="flex items-baseline justify-between gap-x-3">
            <span className="text-body2 text-primary">{step.title}</span>
            <span className="shrink-0 text-body2 text-secondary">
              {step.date.format(DATE_FORMAT)}
            </span>
          </div>
          {step.detail}
        </div>
      </li>
    ))}
  </ol>
);

export const ManageMembershipCard = ({
  disableCancellationAllowed,
  platformUserMembership,
  paymentMethodLast4,
  onUpdatePaymentMethod,
  onKeepMembership,
  onReactivateMembership,
  membershipStatus,
  onRetryMembershipPayment,
  onKeepCurrentMembership,
  isKeepingCurrentMembership = false,
  isRetryingMembershipPayment = false,
  freeze,
  onWithdrawFreezeRequest,
  withdrawPending,
  onResumeMembership,
}: ManageMembershipCardProps) => {
  const { t } = useTranslation();
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const pendingChange = platformUserMembership.pendingChange;

  const {
    name,
    active,
    monthlyCost,
    billingPeriodCost,
    membershipBenefits,
    allowFreeze,
    maxFreezeBillingPeriods,
    commitment = 0,
    billingPeriodInterval,
    billingPeriodIntervalCount,
  } = platformUserMembership.membership;

  const baseBillingPeriodInterval = parseBillingPeriodName(billingPeriodInterval);
  const adjustedCommitment = Math.max(commitment - 1, 0);

  const billingPeriodIntervalName = isFlexibleMembershipsEnabled
    ? (getBillingIntervalBase(baseBillingPeriodInterval) as dayjs.ManipulateType)
    : "month";

  const totalCommitmentPeriod =
    adjustedCommitment * (isFlexibleMembershipsEnabled ? billingPeriodIntervalCount : 1);

  const commitmentExpiration = dayjs(platformUserMembership.created).add(
    totalCommitmentPeriod,
    billingPeriodIntervalName,
  );

  const hasCommitmentPeriod = dayjs().isBefore(commitmentExpiration);
  const isCancellationLocked = hasCommitmentPeriod && disableCancellationAllowed;
  const isExternallyManaged = platformUserMembership.isExternalMembership;
  const status = getMembershipStatus(membershipStatus, active, freeze);
  const canRequestFreeze =
    hasCurrentMembershipBillingPeriod(platformUserMembership) &&
    status == MembershipStatus.active &&
    allowFreeze &&
    maxFreezeBillingPeriods >= 1 &&
    !platformUserMembership.activeMembershipFreezeId &&
    // A freeze voids the renewal invoices a scheduled change applies on, so the
    // backend rejects freezing while one is pending; don't offer the action.
    !pendingChange;
  const numberOfAttempts = 4 - platformUserMembership.dunningStage;
  const addedDunningDaysForNextPayment = getDunningDays(platformUserMembership.dunningStage);
  const addedDunningDaysForLastPayment = getDunningDays(platformUserMembership.dunningStage - 1);

  const lastManualPaymentAttemptDay = dayjs(platformUserMembership.manualPaymentLastAttemptAt);
  const lastAutomaticPaymentAttemptDay = dayjs(platformUserMembership.enteredDunningAt).add(
    addedDunningDaysForLastPayment,
    "day",
  );

  const manageBasePath = `/wallet/memberships/manage/${platformUserMembership.platformUserMembershipId}`;
  const isActiveOrDunning = status == MembershipStatus.active || status == MembershipStatus.dunning;
  const isPastMembership =
    status == MembershipStatus.canceled || status == MembershipStatus.notAvailable;

  const price = isFlexibleMembershipsEnabled
    ? formatBillingPeriodInterval(
        billingPeriodCost,
        baseBillingPeriodInterval,
        billingPeriodIntervalCount,
      )
    : formatBillingPeriodInterval(monthlyCost, "Monthly", 1);

  const statusPill: Record<MembershipStatus, { tone: StatusTone; label: string }> = {
    [MembershipStatus.active]: pendingChange
      ? { tone: "brand", label: t("manageMembershipCard.statusSwitching") }
      : { tone: "brand", label: t("manageMembershipCard.statusActive") },
    [MembershipStatus.dunning]: {
      tone: "alert",
      label: t("manageMembershipCard.statusPaymentFailed"),
    },
    [MembershipStatus.willCancel]: {
      tone: "alert",
      label: t("manageMembershipCard.statusCanceling"),
    },
    [MembershipStatus.canceled]: { tone: "muted", label: t("manageMembershipCard.statusCanceled") },
    [MembershipStatus.notAvailable]: {
      tone: "muted",
      label: t("manageMembershipCard.statusCanceled"),
    },
    [MembershipStatus.freezePending]: {
      tone: "brand",
      label: t("manageMembershipCard.statusFreezePending"),
    },
    [MembershipStatus.freezeScheduled]: {
      tone: "brand",
      label: t("manageMembershipCard.statusFreezeScheduled"),
    },
    [MembershipStatus.frozen]: { tone: "brand", label: t("manageMembershipCard.statusFrozen") },
  };

  const candidateTimelineSteps: (TimelineStep | false | null | undefined)[] = [
    !isExternallyManaged && {
      key: "nextPayment",
      date: dayjs(platformUserMembership.expiration),
      title: t("manageMembershipCard.nextPayment"),
      detail: platformUserMembership.paymentMethodDetails && (
        <span className="text-body3 text-secondary">
          {t("manageMembershipCard.cardEndingIn", {
            last4: platformUserMembership.paymentMethodDetails.last4,
          })}
        </span>
      ),
    },
    pendingChange && {
      key: PENDING_CHANGE_STEP_KEY,
      date: dayjs(pendingChange.expectedEffectiveAt),
      title: t("manageMembershipCard.switchingTo", {
        membershipName: pendingChange.toMembershipName,
      }),
      detail: (
        <button
          type="button"
          className="w-fit text-body3 text-secondary underline disabled:opacity-40"
          disabled={isKeepingCurrentMembership}
          onClick={() => onKeepCurrentMembership(platformUserMembership.platformUserMembershipId)}
        >
          {t("manageMembershipCard.keepCurrentMembership")}
        </button>
      ),
    },
    hasCommitmentPeriod && {
      key: "commitmentEnds",
      date: commitmentExpiration,
      title: t("manageMembershipCard.commitmentEnds"),
    },
  ];
  const timelineSteps = candidateTimelineSteps
    .filter((step): step is TimelineStep => !!step)
    .sort((a, b) => a.date.valueOf() - b.date.valueOf());

  const visibleBenefits = membershipBenefits.slice(0, VISIBLE_BENEFITS_COUNT);
  const hiddenBenefitsCount = membershipBenefits.length - visibleBenefits.length;

  return (
    <div
      className={cx(
        "flex flex-col rounded-xl bg-white text-left shadow-[0px_2px_10px_rgba(0,0,0,0.06)]",
        isPastMembership && "opacity-80",
      )}
      data-testid="manage-membership-card"
      data-platform-user-membership-id={platformUserMembership.platformUserMembershipId}
    >
      <div className="flex flex-col gap-y-1 px-4 pt-4">
        <div className="flex items-start justify-between gap-x-3">
          <h3 className="text-title">{name}</h3>
          <StatusPill tone={statusPill[status].tone}>{statusPill[status].label}</StatusPill>
        </div>
        {/* An external membership is paid to the practice directly, so RepeatMD shows no price. */}
        {!isPastMembership && !isExternallyManaged && (
          <span className="text-body2 text-secondary">{price}</span>
        )}
      </div>

      {!isPastMembership && status != MembershipStatus.dunning && visibleBenefits.length > 0 && (
        <ul className="flex flex-col gap-y-1 px-4 pt-3">
          {visibleBenefits.map((benefit) => (
            <li key={benefit.id} className="flex items-center gap-x-2 text-body2 text-primary">
              <Icon svg={Check} color="brandColor" size="x3small" />
              <span className="line-clamp-1">{benefit.description}</span>
            </li>
          ))}
          {hiddenBenefitsCount > 0 && (
            <li className="pl-5 text-body3 text-secondary">
              {t("manageMembershipCard.moreBenefits", { count: hiddenBenefitsCount })}
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-col gap-y-4 p-4">
        {status == MembershipStatus.notAvailable && (
          <>
            <span className="text-body2 text-secondary">
              {t("manageMembershipCard.membershipCanceledOn", {
                date: dayjs(platformUserMembership.canceledDate).format(DATE_FORMAT),
              })}
            </span>
            <InfoBox text={t("manageMembershipCard.noLongerAvailable")} type="brand" />
          </>
        )}
        {status == MembershipStatus.canceled && (
          <div className="flex items-center justify-between gap-x-3">
            <span className="text-body2 text-secondary">
              {t("manageMembershipCard.membershipCanceledOn", {
                date: dayjs(platformUserMembership.canceledDate).format(DATE_FORMAT),
              })}
            </span>
            <Button size="small" onClick={onReactivateMembership}>
              {t("manageMembershipCard.reactivate")}
            </Button>
          </div>
        )}
        {status == MembershipStatus.willCancel && (
          <div className="flex flex-col items-start gap-y-2">
            <InfoBox
              text={t("manageMembershipCard.cancelingOn", {
                date: dayjs(platformUserMembership.willCancelAt).format(DATE_FORMAT),
              })}
              type="error"
            />
            <TextButton className="!px-0 !py-1 text-bold2" onClick={onKeepMembership}>
              {t("manageMembershipCard.keepMyMembership")}
            </TextButton>
          </div>
        )}
        {status == MembershipStatus.freezePending && freeze && (
          <div className="flex flex-col items-start gap-y-2">
            <InfoBox leftIcon={Snowflake} text="Freeze pending approval" type="brand" />
            <TextButton
              className="!px-0 !py-1 text-bold2"
              disabled={withdrawPending}
              onClick={() =>
                onWithdrawFreezeRequest(
                  platformUserMembership.platformUserMembershipId,
                  freeze.membershipFreezeId,
                )
              }
            >
              {t("manageMembershipCard.withdrawFreezeRequest")}
            </TextButton>
          </div>
        )}
        {status == MembershipStatus.freezeScheduled && freeze && (
          <div className="flex flex-col items-start gap-y-2">
            <InfoBox
              leftIcon={Snowflake}
              text={`Freeze starts ${dayjs(freeze.startsAt).format(DATE_FORMAT)}`}
              type="brand"
            />
            <span className="text-body2 text-secondary">
              {t("manageMembershipCard.freezeScheduledExplanation", {
                date: dayjs(freeze.resumesAt).format(DATE_FORMAT),
              })}
            </span>
            <TextButton
              className="!px-0 !py-1 text-bold2"
              onClick={() =>
                onResumeMembership(
                  platformUserMembership.platformUserMembershipId,
                  freeze.membershipFreezeId,
                )
              }
            >
              {t("manageMembershipCard.cancelScheduledFreeze")}
            </TextButton>
          </div>
        )}
        {status == MembershipStatus.frozen && freeze && (
          <div className="flex flex-col items-start gap-y-3">
            <InfoBox
              leftIcon={Snowflake}
              text={`Frozen until ${dayjs(freeze.resumesAt).format(DATE_FORMAT)}`}
              type="brand"
            />
            <span className="text-body2 text-secondary">
              {t("manageMembershipCard.frozenBillingPaused")}
            </span>
            <Button
              size="small"
              onClick={() =>
                onResumeMembership(
                  platformUserMembership.platformUserMembershipId,
                  freeze.membershipFreezeId,
                )
              }
            >
              {t("manageMembershipCard.resumeMembership")}
            </Button>
          </div>
        )}
        {status == MembershipStatus.dunning && (
          <div className="flex flex-col gap-y-2 rounded-lg bg-red/10 p-3 text-body2 text-red">
            <span className="text-bold2">{t("manageMembershipCard.benefitsDisabled")}</span>
            <span>
              {t("manageMembershipCard.unableToProcessPayment")}{" "}
              {paymentMethodLast4 &&
                `${t("manageMembershipCard.endingIn", { last4: paymentMethodLast4 })} `}
              {t("manageMembershipCard.onDate", {
                date:
                  lastManualPaymentAttemptDay >= lastAutomaticPaymentAttemptDay
                    ? lastManualPaymentAttemptDay.format(DATE_FORMAT)
                    : lastAutomaticPaymentAttemptDay.format(DATE_FORMAT),
              })}{" "}
              {t("manageMembershipCard.willRetryOn", {
                date: dayjs(platformUserMembership.enteredDunningAt)
                  .add(addedDunningDaysForNextPayment, "day")
                  .format(DATE_FORMAT),
              })}
            </span>
            <span>
              {t("manageMembershipCard.paymentAttemptsRemainingPrefix")}{" "}
              <strong>
                {numberOfAttempts} {t("manageMembershipCard.payment")}{" "}
                {t("manageMembershipCard.nounAttempt", { count: numberOfAttempts })}{" "}
              </strong>
              {t("manageMembershipCard.paymentAttemptsRemainingSuffix")}
            </span>
          </div>
        )}
        {status == MembershipStatus.active && timelineSteps.length > 0 && (
          <Timeline steps={timelineSteps} />
        )}
        {/* In dunning the payment box replaces the schedule, but a scheduled switch must stay rescindable. */}
        {status == MembershipStatus.dunning && pendingChange && (
          <Timeline steps={timelineSteps.filter(({ key }) => key === PENDING_CHANGE_STEP_KEY)} />
        )}
        {isActiveOrDunning &&
          (isExternallyManaged ? (
            <span className="text-body2 text-secondary">
              {t("manageMembershipCard.externallyManaged")}
            </span>
          ) : (
            <>
              {status == MembershipStatus.dunning && (
                <div className="flex flex-col gap-y-2">
                  <Button
                    fullWidth
                    disabled={isRetryingMembershipPayment}
                    onClick={() =>
                      onRetryMembershipPayment(platformUserMembership.platformUserMembershipId)
                    }
                  >
                    {t("manageMembershipCard.retryCurrentPaymentMethod")}
                  </Button>
                  <Button
                    fullWidth
                    style="secondary"
                    startIconSVG={Billing}
                    onClick={onUpdatePaymentMethod}
                  >
                    {t("manageMembershipCard.updateCard")}
                  </Button>
                </div>
              )}
              {status == MembershipStatus.active && (
                <div className="flex flex-wrap gap-2">
                  <ActionChip icon={Billing} onClick={onUpdatePaymentMethod}>
                    {t("manageMembershipCard.updateCard")}
                  </ActionChip>
                  {status === MembershipStatus.active &&
                    !pendingChange &&
                    platformUserMembership.hasChangeOptions && (
                      <ActionChip icon={SwapHorizontal} to={`${manageBasePath}/change`}>
                        {t("manageMembershipCard.changePlan")}
                      </ActionChip>
                    )}
                  {canRequestFreeze && (
                    <ActionChip icon={Snowflake} to={`${manageBasePath}/freeze`}>
                      {t("manageMembershipCard.freeze")}
                    </ActionChip>
                  )}
                </div>
              )}
            </>
          ))}
      </div>

      {isActiveOrDunning && !isExternallyManaged && (
        <div className="border-t border-black/5 px-4 py-3">
          {isCancellationLocked ? (
            <p className="text-body3 text-secondary">
              {t("manageMembershipCard.cancelAvailableAfter", {
                date: commitmentExpiration.format(DATE_FORMAT),
              })}
            </p>
          ) : (
            <Link
              to={`${manageBasePath}${status === MembershipStatus.dunning ? "/dunning" : ""}`}
              className="text-body3 text-secondary underline"
            >
              {t("manageMembershipCard.cancelMembership")}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
